import { Box, Checkbox, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import add_all_asset_group from "./images/add_all_asset_group.png";
import admin_console from "./images/admin_console.png";
import asset_groups_selected from "./images/asset_groups_selected.png";
import assign_role_to_user from "./images/assign_role_to_user.png";
import create_new_role from "./images/create_new_role.png";
import create_reader_user from "./images/create_reader_user.png";
import edit_user_button from "./images/edit_user_button.png";
import new_user_form from "./images/new_user_form.png";
import notification_settings from "./images/notification_settings.png";
import permissions_page from "./images/permissions_page.png";
import review_role from "./images/review_role.png";
import role_details from "./images/role_details.png";
import roles_and_scopes from "./images/roles_and_scopes.png";
import user_details from "./images/user_details.png";
import vmdr_button from "./images/vmdr_button.png";
import QualysConnectForm from "./QualysConnectForm";
import QualysConfigPage from "./QualysConfigPage";

const QualysIntegration = () => {
  const colors = useColors();

  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("qualys");
  const [loading, setLoading] = useState(false);
  const [lastAttemptedValues, setLastAttemptedValues] = useState(null);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerQualysIntegration } = useAPI();
  const queryClient = useQueryClient();

  const onSubmit = (values) => {
    setLoading(true);
    setLastAttemptedValues(values);
    registerQualysIntegration(values.hostname, values.username, values.password)
      .then((response) => {
        setLastAttemptedValues(null);
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Qualys integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Qualys and prioritize the suggested remediations."
            : ""
        }
        image={logos.QualysLogo}
        iconBorderRadius={0}
        iconHeight={50}
        iconWidth={43}
        name={"Qualys"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <QualysConfigPage />
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Qualys Console and Navigate to Administration">
                  <Typography variant="body2">
                    Click on the icon at the top left (In this example, VMDR is
                    shown).
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: vmdr_button,
                        altext: "VMDR",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Navigate to the <b>"Platform and Sensor Management"</b> tab
                    and press on <b>ADMIN</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: admin_console,
                        altext: "ADMIN",
                        note: "Click on ADMIN",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Create new Read-Only Role">
                  <Typography variant="body2">
                    Click on the <b>Role Management</b> tab and press on{" "}
                    <b>New Role</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: create_new_role,
                        altext: "Create New Role",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Fill the <b>Role Details</b> - Name the Role <b>ReadOnly</b>{" "}
                    and press <b>Continue</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: role_details,
                        altext: "Role Details",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Select <b>API Access</b> and continue to select module and
                    permissions.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: permissions_page,
                        altext: "Add Permission",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Add the following <strong>modules and permissions</strong>{" "}
                    for the role:
                    <br />
                    <b>NOTE:</b> By default, all permissions are selected -
                    Remove the ones that are not needed.
                  </Typography>
                  <Box
                    sx={{
                      width: "900px",
                      backgroundColor: colors.grey_200,
                      padding: "16px",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color={colors.primary_darker}
                    >
                      Read Permissions
                    </Typography>
                    <Typography variant="body2">
                      Check the box for each completed permission to track the
                      progress
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>
                        Administration &#8594; Action Log Permissions &#8594;
                        Action Log Access{" "}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>
                        Administration &#8594; User Permissions &#8594; Access
                        Role Management Section{" "}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>
                        Administration &#8594; User Permissions &#8594; Read
                        User{" "}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>
                        Vulnerability Management &#8594; TruRisk Report
                        Permissions &#8594; Download TruRisk Reports (All Users)
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>
                        Vulnerability Management &#8594; TruRisk Report
                        Permissions &#8594; Generate, Download, Delete your own
                        TruRisk Reports
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>
                        Vulnerability Management &#8594; VMUI API Permissions
                        &#8594; VMUI API Access
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>
                        Asset Management &#8594; Asset Management Permissions
                        &#8594; Read Asset{" "}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>
                        Asset Management &#8594; Asset Management Permissions
                        &#8594; Access Asset Management module{" "}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2">
                    Press <b>Continue</b> and move to <b>Review And Confirm</b>.
                  </Typography>
                  <Typography variant="body2">
                    Review the role details and press <b>Finish</b>.
                    <br />
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: review_role,
                        altext: "Review Role Details",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Create a New Reader User for Breeze">
                  <Typography variant="body2">
                    Next, click on the <b>User Management</b> tab and press on{" "}
                    <b>Create Reader User</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: create_reader_user,
                        altext: "User Management",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Fill the form. Make sure to use an active email address to
                    get the activation email.
                  </Typography>
                  <Typography variant="body2">
                    <b>NOTE:</b> Ideally, use an existing available email
                    address for this user.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: new_user_form,
                        altext: "New User Form",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Navigate to <b>Asset Groups</b> tab and add the <b>"All"</b>{" "}
                    asset group.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: add_all_asset_group,
                        altext: "Asset Groups",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Make sure the user <b>Asset Groups</b> looks like the
                    following screenshot.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: asset_groups_selected,
                        altext: "Asset Groups Selected",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    The next step is to turn off the user's{" "}
                    <b>Notification Settings</b>.
                  </Typography>
                  <Typography variant="body2">
                    Navigate to <b>Options</b> tab and set All the Notifications
                    Settings to <b>Off/None</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: notification_settings,
                        altext: "Notification Settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Scroll to the bottom and press <b>Save</b>.
                  </Typography>
                  <Typography variant="body2">
                    <b>NOTE</b> The user takes about <b>30 seconds</b> to be
                    created.
                  </Typography>
                  <Typography variant="body2">
                    Back at the <b>User Management</b> page, Refresh the page
                    and copy the <b>Username</b>.
                  </Typography>
                  <Typography variant="body2">
                    Paste it at the bottom of this page.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Assign the Read-Only Role to the new User">
                  <Typography variant="body2">
                    Select the new user and click on <b>Actions &#8594; Edit</b>
                    .
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: edit_user_button,
                        altext: "Edit User Button",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Assign the previously created <b>ReadOnly</b> role to the
                    new user.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: assign_role_to_user,
                        altext: "Assign Role to User",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Check the <b>"Allow user view access to all objects"</b>{" "}
                    box.
                  </Typography>
                  <Typography variant="body2">
                    Make sure the tabs look like the following screenshot.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: roles_and_scopes,
                        altext: "Roles and Scopes",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Press <b>Save</b> to continue.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Activate the new User via Email">
                  <Typography variant="body2">
                    You will receive an email from Qualys to activate the new
                    user and using an <b>OTP Code</b>.
                  </Typography>
                  <Typography variant="body2">
                    Click on <b>Activate Your Account</b>, enter the{" "}
                    <b>OTP Code</b> and press <b>Submit</b>.
                  </Typography>
                  <Typography variant="body2">
                    Once you enter the OTP, the user will be activated and you
                    should see the following details.
                  </Typography>
                  <Typography variant="body2">
                    <b>NOTE:</b> You can see the uncensored <b>Username</b> back
                    at the <b>Activation Email</b> or at the{" "}
                    <b>User Management</b> page.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: user_details,
                        altext: "User Details",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Copy the URL and credentials values to the form below">
                  <Box width={700}>
                    <QualysConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                      initialValues={lastAttemptedValues}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default QualysIntegration;
